// externals
import { Action } from '@interstate/components/Action';
import { Box } from '@interstate/components/Box';
import { ChevronLeftIcon } from '@interstate/components/Icons';
import { RadioButtonList } from '@interstate/components/RadioButtonList';
import { Typography } from '@interstate/components/Typography';
import { SlideOutWithFixedFooter } from '@makemydeal/dr-dash-components';
import { useCallback, useEffect, useState } from 'react';

// types
import type { EditProductPlanProps } from '../../types';

// utils
import { findByPlanKey, getPlanOptionsFromPlans, getProductPlanKey } from '../../utils/editProductUtils';

// types
import type { ProductPlanDetails } from '@makemydeal/dr-dash-types';

const EditProductPlanDrawer = ({
    open,
    productProps,
    plans,
    onClose,
    onEdit,
    hasInapplicablePlan
}: {
    open?: boolean;
    productProps: EditProductPlanProps;
    plans: ProductPlanDetails[];
    onClose: () => void;
    onEdit: (productCode: string, productPlan: string) => void;
    hasInapplicablePlan: boolean;
}) => {
    const [selectedPlanKey, setSelectedPlanKey] = useState<string>(getProductPlanKey(productProps));

    useEffect(() => {
        setSelectedPlanKey(hasInapplicablePlan ? getProductPlanKey(plans[0]) : getProductPlanKey(productProps));
    }, [productProps, plans, hasInapplicablePlan, setSelectedPlanKey]);

    const planOptions = getPlanOptionsFromPlans(plans);

    const handleApply = useCallback(() => {
        const selectedPlanDetails = plans.find(findByPlanKey(selectedPlanKey)) as ProductPlanDetails;
        const changedProductOrPlan = selectedPlanKey !== getProductPlanKey(productProps);

        if (selectedPlanDetails && changedProductOrPlan) {
            onEdit(selectedPlanDetails.productCode, selectedPlanDetails.productProviderPlan);
        }
        onClose();
    }, [productProps, plans, onClose, onEdit, selectedPlanKey]);

    const handleClose = useCallback(() => {
        setSelectedPlanKey(getProductPlanKey(productProps));
        onClose();
    }, [setSelectedPlanKey, productProps, onClose]);

    const isApplyButtonDisabled = hasInapplicablePlan ? false : selectedPlanKey === getProductPlanKey(productProps);

    return (
        <SlideOutWithFixedFooter
            show={open}
            onHide={handleClose}
            position="right"
            header={
                <Action
                    actionIcon={{ icon: <ChevronLeftIcon />, start: true }}
                    onClick={handleClose}
                    size="md"
                    data-testid="edit-product-plan-drawer-back"
                >
                    Edit {productProps.productCategoryCode}
                </Action>
            }
            data-testid="edit-product-plan-drawer"
            panelWidth="min(846px, 95vw)"
            footer={{
                layout: 'rightAligned',
                options: [
                    {
                        action: handleClose,
                        label: 'Cancel',
                        customIdentifiers: {
                            optionButton0: {
                                dataTestId: 'edit-product-plan-drawer-cancel-btn'
                            }
                        }
                    }
                ],
                primary: {
                    action: handleApply,
                    label: 'Save',
                    disabled: isApplyButtonDisabled,
                    customIdentifiers: {
                        primaryButton: {
                            dataTestId: 'edit-product-plan-drawer-apply-btn'
                        }
                    }
                }
            }}
        >
            <Box marginBottom="1rem">
                <Typography variant="strong-md">Plan/Coverage</Typography>
            </Box>
            <RadioButtonList
                label=""
                displayLabel={false}
                data-testid="edit-product-plan-drawer-plan-radio-group"
                onChange={(event) => {
                    if (event.target.value) {
                        setSelectedPlanKey(event.target.value);
                    }
                }}
                options={planOptions}
                value={hasInapplicablePlan ? planOptions[0].value : getProductPlanKey(productProps)}
            />
        </SlideOutWithFixedFooter>
    );
};

export default EditProductPlanDrawer;
